// Server functions for the Journey Through the 47 Gates.
// Records engagement actions, returns per-user progress rows.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { GATE_ACTIONS, GATE_COMPLETION_THRESHOLD, type GateAction } from "@/lib/gate-practice";

function isGateAction(v: unknown): v is GateAction {
  return typeof v === "string" && (GATE_ACTIONS as readonly string[]).includes(v);
}

export interface GateProgressRow {
  gate_n: number;
  first_visited_at: string;
  last_visited_at: string;
  actions: GateAction[];
  completed_at: string | null;
}

export const listGateProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<GateProgressRow[]> => {
    const { data, error } = await context.supabase
      .from("gate_progress")
      .select("gate_n, first_visited_at, last_visited_at, actions, completed_at")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      gate_n: r.gate_n as number,
      first_visited_at: r.first_visited_at as string,
      last_visited_at: r.last_visited_at as string,
      actions: Array.isArray(r.actions) ? (r.actions as unknown[]).filter(isGateAction) : [],
      completed_at: (r.completed_at as string | null) ?? null,
    }));
  });

export const getGateProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { gate_n: number }) => {
    const n = Number(d?.gate_n);
    if (!Number.isInteger(n) || n < 1 || n > 47) throw new Error("Invalid gate");
    return { gate_n: n };
  })
  .handler(async ({ data, context }): Promise<GateProgressRow | null> => {
    const { data: row, error } = await context.supabase
      .from("gate_progress")
      .select("gate_n, first_visited_at, last_visited_at, actions, completed_at")
      .eq("user_id", context.userId)
      .eq("gate_n", data.gate_n)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    return {
      gate_n: row.gate_n as number,
      first_visited_at: row.first_visited_at as string,
      last_visited_at: row.last_visited_at as string,
      actions: Array.isArray(row.actions) ? (row.actions as unknown[]).filter(isGateAction) : [],
      completed_at: (row.completed_at as string | null) ?? null,
    };
  });

export const recordGateAction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { gate_n: number; action: GateAction }) => {
    const n = Number(d?.gate_n);
    if (!Number.isInteger(n) || n < 1 || n > 47) throw new Error("Invalid gate");
    if (!isGateAction(d?.action)) throw new Error("Invalid action");
    return { gate_n: n, action: d.action };
  })
  .handler(async ({ data, context }): Promise<GateProgressRow> => {
    const now = new Date().toISOString();
    const { data: existing } = await context.supabase
      .from("gate_progress")
      .select("gate_n, first_visited_at, last_visited_at, actions, completed_at")
      .eq("user_id", context.userId)
      .eq("gate_n", data.gate_n)
      .maybeSingle();

    const priorActions = Array.isArray(existing?.actions)
      ? (existing!.actions as unknown[]).filter(isGateAction)
      : [];
    const nextActions = priorActions.includes(data.action)
      ? priorActions
      : [...priorActions, data.action];
    const priorCompleted = (existing?.completed_at as string | null) ?? null;
    const completed_at =
      priorCompleted ?? (nextActions.length >= GATE_COMPLETION_THRESHOLD ? now : null);

    const payload = {
      user_id: context.userId,
      gate_n: data.gate_n,
      first_visited_at: existing?.first_visited_at ?? now,
      last_visited_at: now,
      actions: nextActions,
      completed_at,
    };

    const { data: upserted, error } = await context.supabase
      .from("gate_progress")
      .upsert(payload, { onConflict: "user_id,gate_n" })
      .select("gate_n, first_visited_at, last_visited_at, actions, completed_at")
      .single();

    if (error) throw new Error(error.message);

    return {
      gate_n: upserted.gate_n as number,
      first_visited_at: upserted.first_visited_at as string,
      last_visited_at: upserted.last_visited_at as string,
      actions: Array.isArray(upserted.actions)
        ? (upserted.actions as unknown[]).filter(isGateAction)
        : [],
      completed_at: (upserted.completed_at as string | null) ?? null,
    };
  });
